<template>
  <aside class="lms-index-peek" :class="{ 'is-open': !!row }">
    <div class="lms-index-peek__head">
      <div>
        <p class="lms-index-peek__eyebrow">Quick look</p>
        <h3 class="lms-index-peek__title">{{ heading }}</h3>
        <p v-if="detail?.typeTitle" class="lms-index-peek__subtitle">{{ detail.typeTitle }}</p>
      </div>
      <v-btn icon="mdi-close" variant="text" size="small" aria-label="Close peek" @click="$emit('close')" />
    </div>

    <div v-if="row" class="lms-index-peek__body">
      <div v-if="signals.length" class="lms-index-peek__signals">
        <span
          v-for="sig in signals"
          :key="sig.id + sig.label"
          class="lms-index-peek__signal"
          :class="`is-${sig.tone || 'info'}`"
        >
          <span class="lms-index-peek__signal-dot" />
          {{ sig.label }}
        </span>
      </div>

      <div v-if="loading" class="lms-index-peek__loading">
        <v-progress-circular indeterminate color="primary" size="22" />
        <span>Loading record details…</span>
      </div>

      <template v-else-if="hasPeekBody">
        <section v-if="metaRows.length" class="lms-index-peek__section">
          <h4 class="lms-index-peek__section-title">Key facts</h4>
          <div
            v-for="field in metaRows"
            :key="'meta-' + field.key"
            class="lms-index-peek__row"
          >
            <span class="lms-index-peek__label">{{ field.label }}</span>
            <span class="lms-index-peek__value"><LmsShowValue :value="field.value" /></span>
          </div>
        </section>

        <section
          v-for="(section, si) in peekSections"
          :key="section.label + si"
          class="lms-index-peek__section"
        >
          <h4 class="lms-index-peek__section-title">{{ section.label }}</h4>
          <div
            v-for="field in section.fields"
            :key="field.key"
            class="lms-index-peek__row"
          >
            <span class="lms-index-peek__label">{{ field.label }}</span>
            <span class="lms-index-peek__value"><LmsShowValue :value="field.value" /></span>
          </div>
        </section>

        <section v-if="peekRelated.length" class="lms-index-peek__section">
          <h4 class="lms-index-peek__section-title">Related</h4>
          <div class="lms-index-peek__related">
            <div
              v-for="rel in peekRelated"
              :key="rel.label"
              class="lms-index-peek__related-item"
            >
              <span class="lms-index-peek__related-count">{{ rel.count }}</span>
              <span class="lms-index-peek__related-label">{{ rel.label }}</span>
            </div>
          </div>
        </section>
      </template>

      <template v-else>
        <p v-if="loadError" class="lms-index-peek__hint">{{ loadError }}</p>
        <div v-for="col in columns" :key="col.key" class="lms-index-peek__row">
          <span class="lms-index-peek__label">{{ col.title }}</span>
          <span class="lms-index-peek__value">
            <LmsShowValue
              v-if="isStructuredCell(row?.[col.key])"
              :value="row[col.key]"
            />
            <span v-else-if="col.type === 'html'" v-html="row[col.key]" />
            <template v-else>{{ displayCell(row, col) }}</template>
          </span>
        </div>
      </template>
    </div>

    <div class="lms-index-peek__foot">
      <v-btn
        v-if="href"
        color="primary"
        variant="flat"
        block
        :href="href"
      >
        Open full record
      </v-btn>
    </div>
  </aside>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { displayCell } from '../utils/indexCellDisplay'
import LmsShowValue from './LmsShowValue.vue'

const props = defineProps({
  row: { type: Object, default: null },
  columns: { type: Array, default: () => [] },
  href: { type: String, default: '' },
  pluralTitle: { type: String, default: 'Record' }
})
defineEmits(['close'])

function isStructuredCell (value) {
  return value != null && typeof value === 'object' && !Array.isArray(value) && value.kind === 'file'
}

const detail = ref(null)
const loading = ref(false)
const loadError = ref('')
let fetchSeq = 0

const heading = computed(() => {
  if (detail.value?.title) return detail.value.title
  if (!props.row) return props.pluralTitle
  const id = props.row.id
  return id != null ? `${props.pluralTitle.replace(/s$/i, '')} #${id}` : props.pluralTitle
})

const signals = computed(() => {
  if (Array.isArray(detail.value?.signals) && detail.value.signals.length) {
    return detail.value.signals
  }
  return Array.isArray(props.row?._signals) ? props.row._signals : []
})

const metaRows = computed(() => Array.isArray(detail.value?.meta) ? detail.value.meta.slice(0, 6) : [])
const peekSections = computed(() =>
  Array.isArray(detail.value?.sections)
    ? detail.value.sections.slice(0, 2).map((sec) => ({
        ...sec,
        fields: Array.isArray(sec.fields) ? sec.fields.slice(0, 6) : []
      }))
    : []
)
const peekRelated = computed(() =>
  Array.isArray(detail.value?.related) ? detail.value.related.slice(0, 8) : []
)
const hasPeekBody = computed(() =>
  metaRows.value.length > 0 || peekSections.value.length > 0 || peekRelated.value.length > 0
)

watch(
  () => [props.row?.id, props.href],
  async ([id, href]) => {
    detail.value = null
    loadError.value = ''
    if (id == null || !href) {
      loading.value = false
      return
    }

    const seq = ++fetchSeq
    loading.value = true
    try {
      const base = href.includes('.json') ? href : `${href.replace(/\/$/, '')}.json`
      const url = base.includes('?') ? `${base}&peek=1` : `${base}?peek=1`
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (seq !== fetchSeq) return
      detail.value = json
    } catch (_) {
      if (seq !== fetchSeq) return
      loadError.value = 'Could not load full details — showing table columns.'
      detail.value = null
    } finally {
      if (seq === fetchSeq) loading.value = false
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.lms-index-peek {
  position: fixed;
  top: var(--lms-chrome-top, 50px);
  right: 0;
  bottom: var(--lms-chrome-bottom, 0px);
  width: min(440px, 94vw);
  z-index: 1040;
  display: flex;
  flex-direction: column;
  background: var(--app-surface);
  border-left: 1px solid var(--lms-border);
  box-shadow: -18px 0 48px rgba(10, 79, 108, 0.12);
  transform: translateX(105%);
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
}

.lms-index-peek.is-open {
  transform: translateX(0);
}

.lms-index-peek__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 16px 12px;
  border-bottom: 1px solid var(--lms-border);
}

.lms-index-peek__eyebrow {
  margin: 0 0 4px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--lms-primary);
}

.lms-index-peek__title {
  margin: 0;
  font-family: Outfit, 'Source Sans 3', sans-serif;
  font-size: 1.125rem;
  font-weight: 650;
  color: var(--lms-text);
  line-height: 1.3;
}

.lms-index-peek__subtitle {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: var(--lms-text-secondary);
}

.lms-index-peek__body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 12px 16px 20px;
}

.lms-index-peek__loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  color: var(--lms-text-secondary);
  font-size: 0.875rem;
}

.lms-index-peek__hint {
  margin: 0 0 12px;
  font-size: 0.8125rem;
  color: var(--lms-text-secondary);
}

.lms-index-peek__signals {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}

.lms-index-peek__signal {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: #eef6fa;
  color: var(--lms-primary);
}

.lms-index-peek__signal-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.lms-index-peek__signal.is-danger {
  background: #fde8ea;
  color: #c62828;
}

.lms-index-peek__signal.is-warning {
  background: #fff4e5;
  color: #b26a00;
}

.lms-index-peek__signal.is-neutral {
  background: #f0f2f4;
  color: #546e7a;
}

.lms-index-peek__signal.is-info {
  background: #e8f4fb;
  color: #0277bd;
}

.lms-index-peek__section {
  margin-bottom: 18px;
}

.lms-index-peek__section-title {
  margin: 0 0 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--lms-text-secondary);
}

.lms-index-peek__row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #eef2f5;
  font-size: 0.875rem;
}

.lms-index-peek__label {
  color: var(--lms-text-secondary);
  font-weight: 600;
}

.lms-index-peek__value {
  color: var(--lms-text);
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.lms-index-peek__related {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.lms-index-peek__related-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid var(--lms-border);
  border-radius: 10px;
  background: #f7fafc;
}

.lms-index-peek__related-count {
  font-family: Outfit, 'Source Sans 3', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--lms-primary);
  line-height: 1.1;
}

.lms-index-peek__related-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--lms-text-secondary);
}

.lms-index-peek__foot {
  padding: 12px 16px;
  border-top: 1px solid var(--lms-border);
}
</style>
